import Divider from "components/common/Divider";
import LoadingSpinner from "components/common/LoadingSpinner";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Button, Col, Form, Row, Alert } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SocialAuthButtons from "./SocialAuthButtons";
import paths from "routes/paths";
import useLogin from "../../hooks/auth/useLogin";
import { PasswordSetupForm } from "./PasswordSetupForm";

const forgotPasswordPaths = {
  simple: paths.simpleForgotPassword,
  split: paths.splitForgotPassword,
  card: paths.cardForgotPassword,
};

// Helper style function for inputs
const getInputStyles = () => ({
  backgroundColor: "#fff",
  borderColor: "#b0b0b0",
  fontSize: "1rem",
  fontWeight: "400",
  transition: "border-color 0.2s ease",
});

const LoginForm = ({ hasLabel = false, layout = "simple" }) => {
  const navigate = useNavigate();
  const { formData, handleFieldChange, handleSubmit, isLoading } = useLogin();
  const { email, password } = formData;
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidation, setShowValidation] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // new state for Google login
  const [googleUser, setGoogleUser] = useState(null);
  const [googleIdToken, setGoogleIdToken] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);

  // Validation function
  const validateForm = () => {
    const errors = {};

    if (!email.trim()) {
      errors.email = "Please enter your email address";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!password.trim()) {
      errors.password = "Please enter your password";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form submit handler
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (validateForm()) {
      handleSubmit(e);
    }
  };

  // Field change with validation
  const handleFieldChangeWithValidation = (e) => {
    handleFieldChange(e);
    const { name, value } = e.target;

    // Only clear error if field becomes valid
    if (showValidation && validationErrors[name] && value.trim() !== "") {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Input style handlers
  const handleFocus = (e) => {
    e.target.style.borderColor = "#000";
  };
  const handleBlur = (e) => {
    e.target.style.borderColor = "#b0b0b0";
  };
  const handleMouseEnter = (e) => {
    e.target.style.borderColor = "#000";
  };
  const handleMouseLeave = (e) => {
    e.target.style.borderColor = "#b0b0b0";
  };

  const handleGoogleSuccess = (data) => {
    // Save all token data for token refresh system
    sessionStorage.setItem("authToken", data.accessToken);
    sessionStorage.setItem("refreshToken", data.refreshToken);
    sessionStorage.setItem("accessTokenExpiresAt", data.accessTokenExpiresAt);
    sessionStorage.setItem("refreshTokenExpiresAt", data.refreshTokenExpiresAt);
    sessionStorage.setItem("clientId", data.clientId);
    sessionStorage.setItem(
      "userPrivileges",
      JSON.stringify(data.privileges || [])
    );


    window.location.href = "/";
  };

  if (requiresPassword && googleUser) {
    return <PasswordSetupForm userInfo={googleUser} idToken={googleIdToken} />;
  }

  return (
    <Form onSubmit={handleFormSubmit}>
      {/* Email */}
      <Form.Group className="mb-3">
        {hasLabel && (
          <Form.Label className="fw-normal text-dark">Email address</Form.Label>
        )}
        <Form.Control
          placeholder={!hasLabel ? "Email address" : ""}
          value={email}
          name="email"
          onChange={handleFieldChangeWithValidation}
          type="email"
          isInvalid={showValidation && !!validationErrors.email}
          className="form-control-lg custom-shadow"
          style={getInputStyles()}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
        {showValidation && validationErrors.email && (
          <Form.Control.Feedback type="invalid">
            {validationErrors.email}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      {/* Password */}
      <Form.Group className="mb-3">
        {hasLabel && (
          <Form.Label className="fw-normal text-dark">Password</Form.Label>
        )}
        <div className="position-relative">
          <Form.Control
            placeholder={!hasLabel ? "Password" : ""}
            value={password}
            name="password"
            onChange={handleFieldChangeWithValidation}
            type={showPassword ? "text" : "password"}
            isInvalid={showValidation && !!validationErrors.password}
            className="form-control-lg custom-shadow"
            style={{ ...getInputStyles(), paddingRight: "45px" }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
          {!(showValidation && validationErrors.password) && (
            <button
              type="button"
              className="position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                cursor: "pointer",
                right: "15px",
                zIndex: "10",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseLeave={(e) =>
                (e.target.style.backgroundColor = "transparent")
              }
            >
              <FontAwesomeIcon
                icon={showPassword ? "eye-slash" : "eye"}
                className="text-muted"
                style={{
                  fontSize: "16px",
                  color: "#6c757d",
                  transition: "color 0.2s ease",
                }}
              />
            </button>
          )}
          {showValidation && validationErrors.password && (
            <Form.Control.Feedback
              type="invalid"
              style={{ position: "relative", zIndex: "1", display: "block" }}
            >
              {validationErrors.password}
            </Form.Control.Feedback>
          )}
        </div>
      </Form.Group>

      {/* Forgot Password Link */}
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs="auto">
          <Link className="fs-10 mb-0" to={forgotPasswordPaths[layout]}>
            Forgot Password?
          </Link>
        </Col>
      </Row>

      {/* Submit Button */}
      <Form.Group>
        <Button
          type="submit"
          color="primary"
          variant="success"
          className="mt-3 w-100 btn-lg fw-semibold d-flex align-items-center justify-content-center text-sm"
          style={{
            padding: "0.5rem 1.2rem",
            fontWeight: "600",
            textAlign: "center",
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <LoadingSpinner size="sm" text="Logging in..." showText={false} />
          ) : (
            "Log in"
          )}
        </Button>
        {showValidation &&
          (validationErrors.email || validationErrors.password) && (
            <Alert variant="warning" className="mt-2 mb-0">
              <small>Please fill in all required fields to continue.</small>
            </Alert>
          )}
      </Form.Group>

      {/* Social Login */}
      <Divider className="mt-4">or log in with</Divider>
      <SocialAuthButtons onGoogleSuccess={handleGoogleSuccess} />
    </Form>
  );
};

LoginForm.propTypes = {
  layout: PropTypes.string,
  hasLabel: PropTypes.bool,
};

export default LoginForm;
